// components/ui/Input.tsx
// UI Phase 1 primitives: form controls sharing one field treatment.
// Input, Textarea, and Select all use the same border/focus/disabled recipe
// so forms stop re-inventing it per modal.

import { forwardRef } from 'react';
import type {
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { cn } from './cn';

export const FIELD_CLASSES = cn(
  'w-full rounded-control border border-line-soft bg-surface-card text-ink text-sm',
  'placeholder:text-ink-3',
  'transition-colors duration-fast',
  'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-surface-sunken',
  'aria-[invalid=true]:border-status-critical aria-[invalid=true]:focus:ring-status-critical'
);

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...rest }, ref) => (
    <input ref={ref} className={cn(FIELD_CLASSES, 'px-3 py-2', className)} {...rest} />
  )
);
Input.displayName = 'Input';

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, rows = 4, ...rest }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(FIELD_CLASSES, 'px-3 py-2 resize-none', className)}
      {...rest}
    />
  )
);
Textarea.displayName = 'Textarea';

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, children, ...rest }, ref) => (
    <select ref={ref} className={cn(FIELD_CLASSES, 'px-3 py-2 pr-8', className)} {...rest}>
      {children}
    </select>
  )
);
Select.displayName = 'Select';
